import { gsap } from "gsap";
import React, { useRef, useEffect, useState } from "react";
import { useScroll } from "@react-three/drei";
import * as THREE from "three";
import { useFrame, useLoader, useThree } from "@react-three/fiber";
import { easing } from "maath";
import emitter from "../emitter";

export function CameraControl({
  camPosDesk,
  camPosMob,
  camDeskCalc,
  camMobCalc,
  useJoystick = true,
}) {
  const pointer = useRef(null);
  const joystickPosition = useRef({ x: 0, y: 0 });
  const currentPos = useRef(null);
  const currentRot = useRef(null);
  const initialPos = useRef(null);
  const initialRot = useRef(null);
  const previousTouch = useRef(null);
  const infoUp = useRef(false);
  const [usingJoystick, setUsingJoystick] = useState(false);
  const fakeScrollData = useRef({ offset: 0.5, scroll: { current: 0.5 } });
  const lastJoystickMove = useRef(Date.now());
  const joystickActive = useRef(false);

  const { camera, gl } = useThree();
  const data = useScroll();
  const isMobile = window.innerWidth <= 768;

  const mouseMoveEvent = (event) => {
    if (!infoUp.current) {
      let elt = event;

      if (event.touches && event.touches.length > 0) {
        elt = event.touches[0];
      }

      pointer.current.x = (elt.clientX / window.innerWidth) * 2 - 1;
      pointer.current.y = -(elt.clientY / window.innerHeight) * 2 + 1;
    }
  };

  const firstTouchReset = () => {
    if (data) {
      data.offset = 0.5;
      data.scroll.current = 0.5;
      data.el.scrollLeft = data.el.scrollWidth / 2;
    }
    document.removeEventListener("touchstart", firstTouchReset);
  };

  useEffect(() => {
    pointer.current = { x: 0.5, y: 0.5 };

    if (isMobile) {
      if (useJoystick) {
        setUsingJoystick(true);
      } else if (data) {
        data.offset = 0.5;
        data.scroll.current = 0.5;
        data.el.scrollLeft = data.el.scrollWidth / 2;
        document.addEventListener("touchstart", firstTouchReset);
      }
    } else {
      document.addEventListener("mousemove", mouseMoveEvent);
    }

    if (isMobile) {
      currentPos.current = new THREE.Vector3(...camPosMob);
    } else {
      currentPos.current = new THREE.Vector3(...camPosDesk);
    }
    currentRot.current = new THREE.Vector3();
    initialPos.current = currentPos.current.clone();
    initialRot.current = currentRot.current.clone();

    const setInfoState = (infoState) => {
      infoUp.current = infoState;
    };

    const handleJoystickMove = (position) => {
      if (joystickPosition.current) {
        joystickPosition.current.x = position.x;
        joystickPosition.current.y = position.y;
        // Mettre à jour les données de défilement factices pour le joystick
        fakeScrollData.current = {
          offset: 0.5 + position.x * 0.5,
          scroll: { current: 0.5 + position.x * 0.5 }
        };

        // Marquer le joystick comme actif et mettre à jour le timestamp
        joystickActive.current = true;
        lastJoystickMove.current = Date.now();
      }
    };

    emitter.on("setInfoState", setInfoState);
    emitter.on("joystick:move", handleJoystickMove);

    return () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
      document.removeEventListener("touchstart", firstTouchReset);
      emitter.off("joystick:move", handleJoystickMove);
      emitter.off("setInfoState", setInfoState);
    };
  }, []);

  useFrame((state, delta) => {
    if (pointer && pointer.current) {
      if (isMobile) {
        if (usingJoystick) {
          // Utiliser le joystick pour contrôler la caméra avec les données factices
          const camMob = camMobCalc(fakeScrollData.current, currentPos, currentRot);

          // Utiliser un facteur d'amortissement différent selon que le joystick est actif ou non
          // Si le joystick n'a pas été touché depuis un moment, utiliser un amortissement plus fort
          // pour que la caméra reste en place
          const dampFactor = joystickActive.current ? 0.15 : 0.01;

          easing.damp3(state.camera.position, camMob.position, dampFactor, delta);
          easing.damp3(state.camera.rotation, camMob.rotation, dampFactor, delta);
        } else if (data) {
          // Utiliser le défilement pour contrôler la caméra
          const camMob = camMobCalc(data, currentPos, currentRot);
          easing.damp3(state.camera.position, camMob.position, 0.15, delta);
          easing.damp3(state.camera.rotation, camMob.rotation, 0.15, delta);
        }
      } else if (camDeskCalc) {
        const camDesk = camDeskCalc(pointer, currentPos, currentRot);
        // Logique pour les non-mobiles (comme les ordinateurs de bureau)
        easing.damp3(state.camera.position, camDesk.position, 0.5, delta);
        easing.damp3(state.camera.rotation, camDesk.rotation, 0.5, delta);
      }
    }
  });

  return <></>;
}
